import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import type { CustomColDef } from '@shared/components/ui/AgGridTable';
import { faDownload, faEllipsisVertical, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePayerBankNameById } from '@modules/Wallets/BankAccounts/hooks/usePayerBanks';
import { resolvePayerBankName } from '@modules/Wallets/BankAccounts/lib/resolvePayerBankName';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GenerationIdCell } from '../components/display/GenerationIdCell';
import type { WithdrawalPaymentFile } from '../models';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

export interface UseWithdrawalPaymentFilesColumnsOptions {
  onViewDetail?: (file: WithdrawalPaymentFile) => void;
  onDownload?: (file: WithdrawalPaymentFile) => void;
  downloadingFileId?: number | null;
}

export const useWithdrawalPaymentFilesColumns = (options: UseWithdrawalPaymentFilesColumnsOptions = {}) => {
  const { t } = useTranslation('withdrawalPaymentFilesModule');
  const { onViewDetail, onDownload, downloadingFileId } = options;
  const { nameById } = usePayerBankNameById();

  const columnDefs = useMemo<CustomColDef[]>(
    () => [
      {
        colId: 'id',
        field: 'id',
        headerName: t('table.columns.id'),
        width: 64,
      },
      {
        colId: 'generation_id',
        field: 'generation_id',
        headerName: t('table.columns.generation_id'),
        minWidth: 280,
        flex: 2,
        cellRenderer: (params: { data: WithdrawalPaymentFile }) => (
          <GenerationIdCell generationId={params.data.generation_id} />
        ),
      },
      {
        colId: 'payer_bank',
        headerName: t('table.columns.payer_bank'),
        minWidth: 160,
        cellRenderer: (params: { data: WithdrawalPaymentFile }) => (
          <span className="text-sm">
            {resolvePayerBankName(params.data.payer_bank_id, params.data.payer_bank, nameById)}
          </span>
        ),
      },
      {
        colId: 'withdrawals_count',
        field: 'withdrawals_count',
        headerName: t('table.columns.withdrawals_count'),
        minWidth: 120,
        valueGetter: (params) => params.data?.withdrawals_count ?? params.data?.withdrawal_count ?? null,
      },
      {
        colId: 'created_at',
        field: 'created_at',
        headerName: t('table.columns.created_at'),
        minWidth: 160,
        cellRenderer: (params: { data: WithdrawalPaymentFile }) => (
          <span className="text-sm">{formatDate(params.data.created_at)}</span>
        ),
      },
      {
        colId: 'actions',
        headerName: '',
        pinned: 'right',
        width: 65,
        minWidth: 65,
        maxWidth: 65,
        sortable: false,
        suppressMovable: true,
        cellRenderer: (params: { data: WithdrawalPaymentFile }) => {
          const file = params.data;
          const isDownloading = downloadingFileId === file.id;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 px-0"
                  aria-label={t('table.columns.actions')}
                  data-testid={`payment-file-actions-${file.id}`}
                >
                  <FontAwesomeIcon icon={faEllipsisVertical} className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem onClick={() => onViewDetail?.(file)} data-testid={`payment-file-view-${file.id}`}>
                  <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2" />
                  {t('actions.view')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onDownload?.(file)}
                  disabled={isDownloading}
                  data-testid={`payment-file-download-${file.id}`}
                >
                  <FontAwesomeIcon icon={faDownload} className="w-4 h-4 mr-2" />
                  {isDownloading ? t('actions.downloading') : t('actions.download')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [t, onViewDetail, onDownload, downloadingFileId, nameById],
  );

  return { columnDefs };
};
